import React, { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Loader2, Send, Sparkles, Copy, Check } from 'lucide-react';
import { assistants as assistantsApi } from '../../lib/api';
import { aiFailure, aiRequestError } from '../../lib/aiResult';
import { toast } from 'sonner';

// The seven business helpers, grounded in the company knowledge base on the
// backend. Each is picked here, given a message, and returns a drafted reply.
export default function AssistantsPanel() {
  const [roster, setRoster] = useState([]);
  const [active, setActive] = useState(null);
  const [message, setMessage] = useState('');
  const [reply, setReply] = useState('');
  const [source, setSource] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    assistantsApi.list()
      .then((r) => { setRoster(Array.isArray(r) ? r : []); if (r && r[0]) setActive(r[0]); })
      .catch(() => toast.error('Could not load assistants'));
  }, []);

  const run = async () => {
    if (!active || !message.trim()) { toast.error('Pick a helper and type a request'); return; }
    setBusy(true); setReply(''); setSource(''); setNote('');
    try {
      const r = await assistantsApi.run(active.key, message.trim());
      setReply(r.reply || '');
      setSource(r.source || '');
      const why = aiFailure(r);
      if (why) { setNote(why); toast.message(why); }
    } catch (e) {
      const msg = aiRequestError(e);
      setNote(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const copy = () => {
    try { navigator.clipboard.writeText(reply); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* clipboard blocked */ }
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-5 h-5 text-[#8BC53F]" />
        <h1 className="text-2xl font-extrabold text-slate-900">Assistants</h1>
      </div>
      <p className="text-slate-500 mb-5">Specialized helpers trained on your business — billing, content, customer service, dispatch, compliance and more.</p>

      <div className="grid lg:grid-cols-[300px_1fr] gap-5">
        <div className="space-y-2" data-testid="assistant-roster">
          {roster.map((a) => (
            <button
              key={a.key}
              onClick={() => { setActive(a); setReply(''); }}
              className={`w-full text-left p-3 rounded-lg border transition ${active && active.key === a.key ? 'border-[#8BC53F] bg-[#8BC53F]/10' : 'border-slate-200 hover:border-slate-300'}`}
              data-testid={`assistant-${a.key}`}
            >
              <div className="font-bold text-slate-900 text-sm">{a.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{a.tagline}</div>
            </button>
          ))}
          {!roster.length && <div className="text-sm text-slate-400 py-8 text-center">No assistants available.</div>}
        </div>

        <Card className="p-5 border-slate-200">
          {active ? (
            <>
              <div className="font-bold text-slate-900">{active.name}</div>
              <div className="text-xs text-slate-500 mb-3">{active.tagline}</div>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                placeholder={`Ask ${active.name} to draft, summarize, or plan something…`}
                data-testid="assistant-input"
              />
              <div className="flex gap-2 mt-3">
                <Button onClick={run} disabled={busy} className="bg-[#8BC53F] hover:bg-[#6FA82C] text-white font-semibold gap-1.5" data-testid="assistant-run">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Ask
                </Button>
                {busy && <span className="text-xs text-slate-500 self-center">Thinking — a full draft can take up to a minute.</span>}
              </div>
              {note && !busy && (
                <div className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="assistant-note">{note}</div>
              )}
              {reply && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Draft {source === 'template' ? '(fallback — no AI key)' : ''}{source === 'error' ? '(not generated)' : ''}
                    </span>
                    <button onClick={copy} className="text-slate-400 hover:text-slate-700 flex items-center gap-1 text-xs">
                      {copied ? <><Check className="w-3.5 h-3.5" />Copied</> : <><Copy className="w-3.5 h-3.5" />Copy</>}
                    </button>
                  </div>
                  <div className="whitespace-pre-wrap text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="assistant-reply">{reply}</div>
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-slate-400 py-12 text-center">Select an assistant to begin.</div>
          )}
        </Card>
      </div>
    </div>
  );
}
